import { useState, useEffect } from "react";
import { toast } from "sonner";
import { submitSection8 } from "@/lib/api";
import { SECTION8 } from "@/data/content";
import { Check, Menu, X, Globe, Loader2, CheckCircle2 } from "lucide-react";

const IMG = {
  hero: "https://images.unsplash.com/photo-1773427640897-98400b5fe05f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  reno: "https://images.unsplash.com/photo-1618832515490-e181c4794a45?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  about: "https://images.unsplash.com/photo-1765765234094-bc009a3bba62?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export const T = {
  en: {
    nav: ["How It Works", "Services", "Packages", "FAQ"],
    book: "Book a Consultation",
    markets: { label: "Markets we serve", suffix: "Additional markets subject to availability." },
    hero: {
      title: "Your Section 8 Investment Starts With a Clear Plan.",
      sub: "Property analysis, renovation coordination, and rental preparation for investors buying in Pennsylvania — with bilingual support and updates wherever you are.",
      explore: "Explore Packages",
      request: "Request a Consultation",
      highlights: ["English & Portuguese Support", "Support for Remote Investors", "Property Analysis & Renovation Coordination"],
    },
    problems: {
      title: "Buying the Property Is Only the Beginning.",
      cards: [
        { t: "Understand the Numbers", d: "Analyze acquisition costs, rental assumptions, operating expenses, and reserves." },
        { t: "Plan the Renovation", d: "Define repair priorities, budgets, and a practical scope of work." },
        { t: "Coordinate Locally", d: "Organize contractors, project updates, photos, and documentation." },
        { t: "Prepare for Section 8", d: "Navigate administrative steps and coordinate inspection preparation." },
      ],
    },
    how: {
      title: "How It Works",
      steps: ["Define Your Investment Goals.", "Evaluate the Property.", "Plan and Coordinate Improvements.", "Prepare for Rental Approval."],
      note: "Purchase and negotiation are conducted by the investor and the licensed professionals they engage. Rental approval depends on the local housing authority.",
    },
    packages: {
      title: "Packages",
      note: "Packages are alternatives. Service values included are not automatically added together.",
      cards: [
        { name: "Investor Blueprint", price: "$497", desc: "A clear starting point for your rental investment strategy.", cta: "Choose Blueprint",
          inc: ["One consultation of up to 60 minutes.", "Investment budget and property criteria.", "Initial comparison of up to two markets.", "Estimated acquisition, renovation, closing, and reserve requirements.", "Section 8 process overview.", "Written action plan and one revision."] },
        { name: "Property Analysis", price: "$997 per property", desc: "Evaluate a property before committing your capital.", cta: "Analyze My Property",
          inc: ["Analysis of one investor-selected property.", "Rental comparable research and local housing authority parameters.", "Income and expense projections.", "Cash flow, cap rate, and cash-on-cash scenarios.", "Financing and cash purchase scenarios.", "Preliminary renovation estimate based on available information.", "Due diligence checklist.", "Written report and review meeting.", "One update after receiving an inspection report or contractor estimate."],
          note: "Does not replace a professional inspection, appraisal, or title review." },
        { name: "Investment Planning", price: "$2,500", desc: "Compare opportunities and organize your acquisition project.", cta: "Plan My Investment",
          inc: ["Investor Blueprint.", "Analysis of up to three properties supplied by the investor or licensed broker.", "Side-by-side cost and cash flow comparison.", "Property document organization.", "Inspection and estimate scheduling coordination.", "Renovation and rental preparation plan for the selected property.", "Up to four meetings over 60 days."],
          note: "One acquisition project. Brokerage and negotiation services are separate." },
        { name: "Section 8 Ready", price: "$1,750 + renovation project management", desc: "For owners who need help preparing an existing property.", cta: "Prepare My Property",
          inc: ["Initial property condition review.", "Preparation checklist based on applicable local housing authority requirements.", "Repair scope, priorities, and budget planning.", "Contractor proposal organization.", "Administrative support with owner registration and documents.", "Inspection scheduling coordination.", "Coordination of identified corrections and one reinspection.", "Final photos, documents, and completion report."],
          note: "Renovation project management: 18% of the approved renovation budget, with a $3,000 minimum. Smaller or more intensive projects may be quoted at 22%. Scope and pricing are agreed in writing." },
        { name: "Complete Investor Support", price: "$4,500 + renovation project management", desc: "Coordinated support from investment planning to rental preparation.", cta: "Request Complete Support", badge: "Comprehensive Support",
          inc: ["Investor Blueprint.", "Analysis of up to three properties.", "Acquisition project coordination with the investor's broker, title company, and inspector.", "Renovation planning for one acquired property.", "Section 8 Ready services.", "Weekly reports during active project execution.", "Photo, video, invoice, and draw request organization.", "Final handoff to the owner or property manager.", "Up to 30 days of administrative support after preparation.", "Up to 120 days of total project support."],
          note: "The $4,500 package fee replaces the fees of included packages. Renovation project management is charged separately. Extensions require a separate quote." },
      ],
    },
    addons: {
      title: "Additional Services",
      items: [
        "Additional property analysis: $450.",
        "Property visit with photos, videos, and condition report: $250 plus travel.",
        "Invoice and draw documentation organization: $200 per request.",
        "Administrative onboarding support for a ready property: $750.",
        "Additional reinspection coordination: $200 plus travel.",
        "Monthly investor report using property manager-provided data: $150 per property.",
        "Portfolio study for up to five properties: starting at $3,500.",
      ],
      note: "Property visits and condition reports are not certified technical inspections.",
    },
    fees: {
      title: "Understand Your Service Costs.",
      lines: ["Complete Investor Support: $4,500.", "Approved renovation budget: $30,000.", "Project management at 18%: $5,400.", "Total Vertexhub service fees: $9,900."],
      disclaimer: "The $30,000 renovation budget is separate from the $9,900 service fees. Property purchase, closing costs, labor, materials, permits, inspections, financing, and third-party charges are not included.",
    },
    about: {
      title: "Practical Support for Property Investors.",
      text: "Vertexhub Ventures supports investors with property analysis, renovation planning, project coordination, and Section 8 rental preparation. Our approach combines organized execution, clear documentation, and bilingual communication for investors managing projects locally or remotely.",
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        { q: "Do you guarantee Section 8 approval?", a: "No. Approval depends on the local housing authority, property condition, required documentation, and applicable program requirements." },
        { q: "Do you guarantee a rental amount or investment return?", a: "No. Rental figures and financial projections are estimates. Approved rent, expenses, occupancy, financing, and market conditions can affect results." },
        { q: "Can I use your services if I already own a property?", a: "Yes. Section 8 Ready is designed for owners preparing an existing property." },
        { q: "Can you help if I live outside Pennsylvania or outside the United States?", a: "Yes. Remote investors can receive bilingual communication, project reports, photos, and documentation. On-site availability depends on the property's location." },
        { q: "Are renovation costs included?", a: "No. Renovation work, materials, permits, and third-party expenses are paid separately by the investor." },
        { q: "Do you provide brokerage or property management?", a: "Brokerage, purchase negotiations, tenant placement, lease negotiations, and ongoing property management are handled by appropriately licensed professionals under separate agreements." },
        { q: "Can I upgrade my package?", a: "Fees already paid may be credited toward an upgrade within 60 days for the same project, subject to a written agreement." },
      ],
    },
    form: {
      title: "Tell Us About Your Investment.",
      name: "Full name", email: "Email", phone: "Phone / WhatsApp (optional)",
      lang: "Preferred language", langOpts: ["English", "Portuguese"],
      loc: "Investor location", target: "Target property city and state",
      budget: "Estimated total investment budget",
      budgetOpts: ["Under $75,000", "$75,000–$150,000", "$150,000–$250,000", "$250,000+", "Not sure yet"],
      owner: "Do you already own or have a property under contract?",
      ownerOpts: ["Still exploring", "Property identified", "Under contract", "Already own a property"],
      pkg: "Interested package",
      pkgOpts: ["Investor Blueprint", "Property Analysis", "Investment Planning", "Section 8 Ready", "Complete Investor Support", "Not sure yet"],
      message: "Message",
      consent: "I authorize Vertexhub Ventures to contact me about this inquiry. I am not being enrolled in marketing.",
      submit: "Request My Consultation",
      success: "Thank you. Your consultation request has been received. We'll be in touch soon.",
      error: "Something went wrong. Please review the form and try again.",
      required: "Please complete the required fields and authorize contact.",
    },
    footer: {
      tagline: "Property Analysis • Renovation Coordination • Section 8 Rental Preparation.",
      disclaimer: "Vertexhub Ventures is an independent service provider and is not affiliated with HUD or any public housing authority. Services do not guarantee program approval, occupancy, rental amounts, financing, or investment returns. Regulated real estate services are provided by appropriately licensed professionals under separate agreements.",
      pricing: "Prices are subject to scope and a written agreement.",
    },
  },
  pt: {
    nav: ["Como Funciona", "Serviços", "Pacotes", "FAQ"],
    book: "Agendar Consultoria",
    markets: { label: "Mercados atendidos", suffix: "Outros mercados sujeitos à disponibilidade." },
    hero: {
      title: "Seu Investimento no Section 8 Começa Com um Plano Claro.",
      sub: "Análise de imóveis, coordenação de reformas e preparação para locação para investidores que compram na Pensilvânia — com suporte bilíngue e atualizações onde você estiver.",
      explore: "Ver Pacotes",
      request: "Solicitar Consultoria",
      highlights: ["Atendimento em Inglês e Português", "Suporte para Investidores à Distância", "Análise de Imóveis & Coordenação de Reformas"],
    },
    problems: {
      title: "Comprar o Imóvel É Só o Começo.",
      cards: [
        { t: "Entenda os Números", d: "Analise custos de aquisição, premissas de locação, despesas operacionais e reservas." },
        { t: "Planeje a Reforma", d: "Defina prioridades de reparo, orçamentos e um escopo de trabalho prático." },
        { t: "Coordene Localmente", d: "Organize prestadores, atualizações do projeto, fotos e documentação." },
        { t: "Prepare para o Section 8", d: "Conduza as etapas administrativas e coordene a preparação para a inspeção." },
      ],
    },
    how: {
      title: "Como Funciona",
      steps: ["Defina Seus Objetivos de Investimento.", "Avalie o Imóvel.", "Planeje e Coordene as Melhorias.", "Prepare para a Aprovação de Locação."],
      note: "A compra e a negociação são conduzidas pelo investidor e pelos profissionais licenciados contratados. A aprovação da locação depende da autoridade habitacional local.",
    },
    packages: {
      title: "Pacotes",
      note: "Os pacotes são alternativas. Os valores dos serviços incluídos não são somados automaticamente.",
      cards: [
        { name: "Investor Blueprint", price: "US$ 497", desc: "Um ponto de partida claro para sua estratégia de investimento em locação.", cta: "Escolher Blueprint",
          inc: ["Uma consultoria de até 60 minutos.", "Orçamento de investimento e critérios de imóvel.", "Comparação inicial de até dois mercados.", "Estimativa de aquisição, reforma, fechamento e reservas.", "Visão geral do processo Section 8.", "Plano de ação por escrito e uma revisão."] },
        { name: "Property Analysis", price: "US$ 997 por imóvel", desc: "Avalie um imóvel antes de comprometer seu capital.", cta: "Analisar Meu Imóvel",
          inc: ["Análise de um imóvel escolhido pelo investidor.", "Pesquisa de comparáveis de locação e parâmetros da autoridade habitacional local.", "Projeções de receita e despesa.", "Cenários de fluxo de caixa, cap rate e cash-on-cash.", "Cenários de financiamento e compra à vista.", "Estimativa preliminar de reforma com base nas informações disponíveis.", "Checklist de due diligence.", "Relatório por escrito e reunião de revisão.", "Uma atualização após receber laudo de inspeção ou orçamento de prestador."],
          note: "Não substitui inspeção profissional, avaliação ou análise de título." },
        { name: "Investment Planning", price: "US$ 2.500", desc: "Compare oportunidades e organize seu projeto de aquisição.", cta: "Planejar Meu Investimento",
          inc: ["Investor Blueprint.", "Análise de até três imóveis fornecidos pelo investidor ou corretor licenciado.", "Comparação lado a lado de custos e fluxo de caixa.", "Organização de documentos do imóvel.", "Coordenação de agendamento de inspeção e orçamentos.", "Plano de reforma e preparação para locação do imóvel selecionado.", "Até quatro reuniões em 60 dias."],
          note: "Um projeto de aquisição. Serviços de corretagem e negociação são separados." },
        { name: "Section 8 Ready", price: "US$ 1.750 + gestão do projeto de reforma", desc: "Para proprietários que precisam de ajuda para preparar um imóvel existente.", cta: "Preparar Meu Imóvel",
          inc: ["Revisão inicial da condição do imóvel.", "Checklist de preparação com base nos requisitos aplicáveis da autoridade habitacional local.", "Escopo de reparos, prioridades e planejamento de orçamento.", "Organização de propostas de prestadores.", "Suporte administrativo com registro do proprietário e documentos.", "Coordenação de agendamento da inspeção.", "Coordenação das correções identificadas e uma reinspeção.", "Fotos finais, documentos e relatório de conclusão."],
          note: "Gestão do projeto de reforma: 18% do orçamento de reforma aprovado, com mínimo de US$ 3.000. Projetos menores ou mais intensivos podem ser cotados a 22%. Escopo e preço acordados por escrito." },
        { name: "Complete Investor Support", price: "US$ 4.500 + gestão do projeto de reforma", desc: "Suporte coordenado do planejamento do investimento à preparação para locação.", cta: "Solicitar Suporte Completo", badge: "Suporte Abrangente",
          inc: ["Investor Blueprint.", "Análise de até três imóveis.", "Coordenação do projeto de aquisição com o corretor, a title company e o inspetor do investidor.", "Planejamento de reforma para um imóvel adquirido.", "Serviços Section 8 Ready.", "Relatórios semanais durante a execução ativa do projeto.", "Organização de fotos, vídeos, notas fiscais e solicitações de draw.", "Entrega final ao proprietário ou administradora.", "Até 30 dias de suporte administrativo após a preparação.", "Até 120 dias de suporte total ao projeto."],
          note: "A taxa de US$ 4.500 substitui as taxas dos pacotes incluídos. A gestão do projeto de reforma é cobrada separadamente. Prorrogações exigem orçamento separado." },
      ],
    },
    addons: {
      title: "Serviços Adicionais",
      items: [
        "Análise adicional de imóvel: US$ 450.",
        "Visita ao imóvel com fotos, vídeos e relatório de condição: US$ 250 mais deslocamento.",
        "Organização de notas fiscais e documentação de draw: US$ 200 por solicitação.",
        "Suporte administrativo de onboarding para imóvel pronto: US$ 750.",
        "Coordenação de reinspeção adicional: US$ 200 mais deslocamento.",
        "Relatório mensal ao investidor com dados fornecidos pela administradora: US$ 150 por imóvel.",
        "Estudo de portfólio de até cinco imóveis: a partir de US$ 3.500.",
      ],
      note: "Visitas e relatórios de condição não são inspeções técnicas certificadas.",
    },
    fees: {
      title: "Entenda os Custos dos Serviços.",
      lines: ["Complete Investor Support: US$ 4.500.", "Orçamento de reforma aprovado: US$ 30.000.", "Gestão do projeto a 18%: US$ 5.400.", "Total de honorários Vertexhub: US$ 9.900."],
      disclaimer: "O orçamento de reforma de US$ 30.000 é separado dos honorários de US$ 9.900. Compra do imóvel, custos de fechamento, mão de obra, materiais, licenças, inspeções, financiamento e cobranças de terceiros não estão incluídos.",
    },
    about: {
      title: "Suporte Prático para Investidores Imobiliários.",
      text: "A Vertexhub Ventures apoia investidores com análise de imóveis, planejamento de reformas, coordenação de projetos e preparação para locação pelo Section 8. Nossa abordagem combina execução organizada, documentação clara e comunicação bilíngue para investidores que gerenciam projetos local ou remotamente.",
    },
    faq: {
      title: "Perguntas Frequentes",
      items: [
        { q: "Vocês garantem a aprovação no Section 8?", a: "Não. A aprovação depende da autoridade habitacional local, da condição do imóvel, da documentação exigida e dos requisitos aplicáveis do programa." },
        { q: "Vocês garantem um valor de locação ou retorno do investimento?", a: "Não. Valores de locação e projeções financeiras são estimativas. Aluguel aprovado, despesas, ocupação, financiamento e condições de mercado podem afetar os resultados." },
        { q: "Posso usar os serviços se já tenho um imóvel?", a: "Sim. O Section 8 Ready foi feito para proprietários que preparam um imóvel existente." },
        { q: "Vocês ajudam se eu morar fora da Pensilvânia ou dos Estados Unidos?", a: "Sim. Investidores à distância recebem comunicação bilíngue, relatórios do projeto, fotos e documentação. A disponibilidade presencial depende da localização do imóvel." },
        { q: "Os custos de reforma estão incluídos?", a: "Não. Obra de reforma, materiais, licenças e despesas de terceiros são pagos separadamente pelo investidor." },
        { q: "Vocês oferecem corretagem ou administração de imóveis?", a: "Corretagem, negociação de compra, colocação de inquilinos, negociação de contratos e administração contínua são conduzidas por profissionais licenciados sob contratos separados." },
        { q: "Posso fazer upgrade do meu pacote?", a: "Valores já pagos podem ser creditados em um upgrade dentro de 60 dias para o mesmo projeto, mediante acordo por escrito." },
      ],
    },
    form: {
      title: "Conte-nos Sobre Seu Investimento.",
      name: "Nome completo", email: "Email", phone: "Telefone / WhatsApp (opcional)",
      lang: "Idioma preferido", langOpts: ["Inglês", "Português"],
      loc: "Localização do investidor", target: "Cidade e estado do imóvel-alvo",
      budget: "Orçamento total estimado de investimento",
      budgetOpts: ["Abaixo de US$ 75.000", "US$ 75.000–150.000", "US$ 150.000–250.000", "US$ 250.000+", "Ainda não sei"],
      owner: "Você já possui ou tem um imóvel sob contrato?",
      ownerOpts: ["Ainda explorando", "Imóvel identificado", "Sob contrato", "Já possuo um imóvel"],
      pkg: "Pacote de interesse",
      pkgOpts: ["Investor Blueprint", "Property Analysis", "Investment Planning", "Section 8 Ready", "Complete Investor Support", "Ainda não sei"],
      message: "Mensagem",
      consent: "Autorizo a Vertexhub Ventures a entrar em contato sobre esta solicitação. Não estou sendo inscrito em marketing.",
      submit: "Solicitar Minha Consultoria",
      success: "Obrigado. Sua solicitação de consultoria foi recebida. Entraremos em contato em breve.",
      error: "Algo deu errado. Revise o formulário e tente novamente.",
      required: "Preencha os campos obrigatórios e autorize o contato.",
    },
    footer: {
      tagline: "Análise de Imóveis • Coordenação de Reformas • Preparação para Locação Section 8.",
      disclaimer: "A Vertexhub Ventures é uma prestadora de serviços independente e não é afiliada ao HUD nem a qualquer autoridade habitacional pública. Os serviços não garantem aprovação no programa, ocupação, valores de aluguel, financiamento ou retorno de investimento. Serviços imobiliários regulados são prestados por profissionais licenciados sob contratos separados.",
      pricing: "Preços sujeitos ao escopo e a contrato escrito.",
    },
  },
};

const navy = "#0B132B";
const gold = "#C9A227";

export default function Section8() {
  const [lang, setLang] = useState("en");
  const [menuOpen, setMenuOpen] = useState(false);
  const t = T[lang];
  const book = SECTION8.bookingUrl;
  const bookProps = book ? { href: book, target: "_blank", rel: "noopener noreferrer" } : { href: "#contact" };

  useEffect(() => {
    document.title = lang === "en"
      ? "Section 8 Investment Support | Vertexhub Ventures"
      : "Suporte a Investimento Section 8 | Vertexhub Ventures";
  }, [lang]);

  const anchors = ["#how-it-works", "#services", "#packages", "#faq"];

  return (
    <div className="min-h-screen bg-white text-[#0B132B]" style={{ scrollBehavior: "smooth" }}>
      {/* HEADER */}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 lg:px-8">
          <a href="#top" className="font-display text-lg font-bold tracking-wide" style={{ color: navy }}>
            Vertexhub <span style={{ color: gold }}>Ventures</span>
          </a>
          <nav className="hidden items-center gap-6 md:flex">
            {t.nav.map((label, i) => (
              <a key={label} href={anchors[i]} className="text-sm font-medium text-slate-600 transition-colors hover:text-[#0B132B]">{label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLang(lang === "en" ? "pt" : "en")}
              data-testid="lang-toggle"
              aria-label="Toggle language"
              className="flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-400"
            >
              <Globe className="h-3.5 w-3.5" /> {lang === "en" ? "PT" : "EN"}
            </button>
            <a {...bookProps} className="hidden rounded-full px-4 py-2 text-sm font-semibold text-white sm:inline-block" style={{ background: navy }}>
              {t.book}
            </a>
            <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="border-t border-slate-200 bg-white px-4 py-3 md:hidden">
            {t.nav.map((label, i) => (
              <a key={label} href={anchors[i]} onClick={() => setMenuOpen(false)} className="block py-2 text-sm font-medium text-slate-700">{label}</a>
            ))}
            <a {...bookProps} onClick={() => setMenuOpen(false)} className="mt-2 block rounded-full px-4 py-2 text-center text-sm font-semibold text-white" style={{ background: navy }}>{t.book}</a>
          </div>
        )}
      </header>

      <main id="top">
        {/* HERO */}
        <section className="relative overflow-hidden" style={{ background: navy }}>
          <div className="absolute inset-0">
            <img src={IMG.hero} alt="" className="h-full w-full object-cover opacity-25" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,19,43,0.82), rgba(11,19,43,0.92))" }} />
          </div>
          <div className="relative mx-auto max-w-6xl px-4 py-24 lg:px-8 lg:py-32">
            <h1 className="max-w-3xl font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl">{t.hero.title}</h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-200">{t.hero.sub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#packages" className="rounded-full px-7 py-3.5 text-sm font-semibold" style={{ background: gold, color: navy }}>{t.hero.explore}</a>
              <a {...bookProps} className="rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold text-white hover:bg-white/10">{t.hero.request}</a>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
              {t.hero.highlights.map((h) => (
                <span key={h} className="flex items-center gap-2 text-sm text-slate-200"><Check className="h-4 w-4" style={{ color: gold }} /> {h}</span>
              ))}
            </div>
            <div className="mt-8 inline-flex flex-wrap items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-xs text-slate-300">
              <span className="font-semibold" style={{ color: gold }}>{t.markets.label}:</span>
              {SECTION8.markets.join(" • ")}
              <span className="text-slate-400">— {t.markets.suffix}</span>
            </div>
          </div>
        </section>

        {/* PROBLEMS / SERVICES */}
        <section id="services" className="py-20 lg:py-24">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <h2 className="text-center font-display text-3xl font-bold" style={{ color: navy }}>{t.problems.title}</h2>
            <div className="mx-auto mt-4 h-px w-20" style={{ background: gold }} />
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {t.problems.cards.map((c, i) => (
                <div key={i} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
                  <span className="font-display text-2xl font-bold" style={{ color: gold }}>{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 text-base font-semibold" style={{ color: navy }}>{c.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how-it-works" className="border-y border-slate-200 bg-[#F6F7F9] py-20 lg:py-24">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-center">
              <div>
                <h2 className="font-display text-3xl font-bold" style={{ color: navy }}>{t.how.title}</h2>
                <ol className="mt-8 space-y-5">
                  {t.how.steps.map((s, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: navy }}>{i + 1}</span>
                      <span className="pt-1.5 text-sm font-medium text-slate-700">{s}</span>
                    </li>
                  ))}
                </ol>
                <p className="mt-8 rounded-xl border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-600">{t.how.note}</p>
              </div>
              <img src={IMG.reno} alt="" className="rounded-2xl border border-slate-200 object-cover shadow-sm" />
            </div>
          </div>
        </section>

        {/* PACKAGES */}
        <section id="packages" className="py-20 lg:py-24">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <h2 className="text-center font-display text-3xl font-bold" style={{ color: navy }}>{t.packages.title}</h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-slate-500">{t.packages.note}</p>
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {t.packages.cards.map((p, i) => (
                <div key={i} className={`relative flex flex-col rounded-2xl border p-7 shadow-sm ${p.badge ? "border-2" : "border-slate-200"}`} style={p.badge ? { borderColor: gold } : {}}>
                  {p.badge && (
                    <span className="absolute -top-3 left-7 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white" style={{ background: navy }}>{p.badge}</span>
                  )}
                  <h3 className="text-lg font-bold" style={{ color: navy }}>{p.name}</h3>
                  <div className="mt-2 text-xl font-bold" style={{ color: gold }}>{p.price}</div>
                  <p className="mt-2 text-sm text-slate-600">{p.desc}</p>
                  <ul className="mt-5 flex-1 space-y-2">
                    {p.inc.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-sm text-slate-700"><Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: gold }} /> {it}</li>
                    ))}
                  </ul>
                  {p.note && <p className="mt-4 rounded-lg bg-[#F6F7F9] p-3 text-xs leading-relaxed text-slate-500">{p.note}</p>}
                  <a {...bookProps} className="mt-6 block rounded-full py-3 text-center text-sm font-semibold text-white" style={{ background: navy }}>{p.cta}</a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ADD-ONS */}
        <section className="border-t border-slate-200 bg-[#F6F7F9] py-20 lg:py-24">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            <h2 className="font-display text-3xl font-bold" style={{ color: navy }}>{t.addons.title}</h2>
            <ul className="mt-8 space-y-3">
              {t.addons.items.map((it) => (
                <li key={it} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700"><Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: gold }} /> {it}</li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-slate-500">{t.addons.note}</p>
          </div>
        </section>

        {/* FEE EXAMPLE */}
        <section className="py-20 lg:py-24">
          <div className="mx-auto max-w-3xl px-4 lg:px-8">
            <h2 className="font-display text-3xl font-bold" style={{ color: navy }}>{t.fees.title}</h2>
            <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200">
              {t.fees.lines.map((l, i) => (
                <div key={i} className={`px-6 py-4 text-sm ${i % 2 ? "bg-white" : "bg-[#F6F7F9]"} ${i === t.fees.lines.length - 1 ? "font-bold" : ""}`} style={i === t.fees.lines.length - 1 ? { color: navy } : { color: "#475569" }}>{l}</div>
              ))}
            </div>
            <p className="mt-6 rounded-xl border border-slate-200 bg-[#F6F7F9] p-4 text-sm leading-relaxed text-slate-600">{t.fees.disclaimer}</p>
          </div>
        </section>

        {/* ABOUT */}
        <section className="border-y border-slate-200 bg-[#F6F7F9] py-20 lg:py-24">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
              <img src={IMG.about} alt="" className="rounded-2xl border border-slate-200 object-cover shadow-sm" />
              <div>
                <h2 className="font-display text-3xl font-bold" style={{ color: navy }}>{t.about.title}</h2>
                <div className="mt-4 h-px w-20" style={{ background: gold }} />
                <p className="mt-6 text-base leading-relaxed text-slate-600">{t.about.text}</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-20 lg:py-24">
          <div className="mx-auto max-w-3xl px-4 lg:px-8">
            <h2 className="text-center font-display text-3xl font-bold" style={{ color: navy }}>{t.faq.title}</h2>
            <div className="mt-10 space-y-3">
              {t.faq.items.map((f, i) => (
                <details key={i} className="group rounded-xl border border-slate-200 bg-white p-5" data-testid={`s8-faq-${i}`}>
                  <summary className="cursor-pointer list-none text-sm font-semibold marker:hidden" style={{ color: navy }}>{f.q}</summary>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CONTACT FORM */}
        <ContactForm t={t.form} />
      </main>

      {/* FOOTER */}
      <footer className="py-12" style={{ background: navy }}>
        <div className="mx-auto max-w-6xl px-4 text-center lg:px-8">
          <div className="font-display text-lg font-bold text-white">Vertexhub <span style={{ color: gold }}>Ventures</span></div>
          <p className="mt-2 text-sm text-slate-300">{t.footer.tagline}</p>
          <p className="mx-auto mt-6 max-w-3xl text-xs leading-relaxed text-slate-400">{t.footer.disclaimer}</p>
          <p className="mt-4 text-xs text-slate-500">{t.footer.pricing}</p>
        </div>
      </footer>
    </div>
  );
}

function ContactForm({ t }) {
  const empty = { full_name: "", email: "", phone: "", preferred_language: "English", investor_location: "", target_city_state: "", budget_range: "", ownership_status: "", interested_package: "", message: "", consent: false, company_website: "" };
  const [f, setF] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const inp = "w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-[#0B132B] focus:border-[#C9A227] focus:outline-none focus:ring-1 focus:ring-[#C9A227]";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!f.full_name.trim() || !f.email.trim() || !f.consent) {
      toast.error(t.required);
      return;
    }
    setSending(true);
    try {
      await submitSection8(f);
      setDone(true);
      toast.success(t.success);
    } catch {
      toast.error(t.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="border-t border-slate-200 bg-[#F6F7F9] py-20 lg:py-24">
      <div className="mx-auto max-w-2xl px-4 lg:px-8">
        <h2 className="text-center font-display text-3xl font-bold" style={{ color: navy }}>{t.title}</h2>
        {done ? (
          <div className="mt-10 flex flex-col items-center rounded-2xl border border-slate-200 bg-white p-10 text-center">
            <CheckCircle2 className="h-14 w-14" style={{ color: gold }} />
            <p className="mt-5 text-sm text-slate-600">{t.success}</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="section8-contact-form" className="mt-10 grid gap-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:grid-cols-2 sm:p-8">
            <div className="hidden" aria-hidden="true">
              <label>Company Website<input tabIndex={-1} autoComplete="off" value={f.company_website} onChange={(e) => set("company_website", e.target.value)} /></label>
            </div>
            <Field label={t.name} req><input className={inp} data-testid="s8-name" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} required /></Field>
            <Field label={t.email} req><input type="email" className={inp} data-testid="s8-email" value={f.email} onChange={(e) => set("email", e.target.value)} required /></Field>
            <Field label={t.phone}><input className={inp} data-testid="s8-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label={t.lang}><select className={inp} value={f.preferred_language} onChange={(e) => set("preferred_language", e.target.value)}>{t.langOpts.map((o) => <option key={o}>{o}</option>)}</select></Field>
            <Field label={t.loc}><input className={inp} value={f.investor_location} onChange={(e) => set("investor_location", e.target.value)} /></Field>
            <Field label={t.target}><input className={inp} value={f.target_city_state} onChange={(e) => set("target_city_state", e.target.value)} /></Field>
            <Field label={t.budget} full><select className={inp} data-testid="s8-budget" value={f.budget_range} onChange={(e) => set("budget_range", e.target.value)}><option value="">—</option>{t.budgetOpts.map((o) => <option key={o}>{o}</option>)}</select></Field>
            <Field label={t.owner} full><select className={inp} value={f.ownership_status} onChange={(e) => set("ownership_status", e.target.value)}><option value="">—</option>{t.ownerOpts.map((o) => <option key={o}>{o}</option>)}</select></Field>
            <Field label={t.pkg} full><select className={inp} value={f.interested_package} onChange={(e) => set("interested_package", e.target.value)}><option value="">—</option>{t.pkgOpts.map((o) => <option key={o}>{o}</option>)}</select></Field>
            <Field label={t.message} full><textarea rows={4} className={inp} value={f.message} onChange={(e) => set("message", e.target.value)} /></Field>
            <label className="flex items-start gap-2 text-sm text-slate-600 sm:col-span-2">
              <input type="checkbox" data-testid="s8-consent" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#0B132B]" />
              <span>{t.consent}</span>
            </label>
            <button type="submit" disabled={sending} data-testid="section8-submit-btn" className="flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold text-white disabled:opacity-60 sm:col-span-2" style={{ background: navy }}>
              {sending && <Loader2 className="h-4 w-4 animate-spin" />}{sending ? "…" : t.submit}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

function Field({ label, children, full, req }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="mb-1.5 block text-sm font-medium text-slate-600">{label}{req && <span style={{ color: gold }}> *</span>}</label>
      {children}
    </div>
  );
}
